import api from './client';

/**
 * Documents API calls
 */
export const documentsAPI = {
  // Legal documents (TipTap editor)
  /**
   * Get all legal documents
   */
  getDocuments: async () => {
    const response = await api.get('/api/documents/legal-documents/');
    return response.data;
  },

  /**
   * Get single legal document
   */
  getDocument: async (id: number) => {
    const response = await api.get(`/api/documents/legal-documents/${id}/`);
    return response.data;
  },

  /**
   * Create new legal document
   */
  createDocument: async (documentData: any) => {
    const response = await api.post('/api/documents/legal-documents/', documentData);
    return response.data;
  },

  /**
   * Update legal document
   */
  updateDocument: async (id: number, documentData: any) => {
    const response = await api.put(`/api/documents/legal-documents/${id}/`, documentData);
    return response.data;
  },

  /**
   * Delete legal document
   */
  deleteLegalDocument: async (id: number) => {
    const response = await api.delete(`/api/documents/legal-documents/${id}/`);
    return response.data;
  },

  /**
   * Preview PDF for a legal document (returns blob)
   */
  previewPdf: async (id: number) => {
    const response = await api.post(`/api/documents/legal-documents/${id}/preview/`, {}, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Generate and save PDF for a legal document
   */
  generatePdf: async (id: number) => {
    const response = await api.post(`/api/documents/legal-documents/${id}/generate-pdf/`);
    return response.data;
  },

  /**
   * Get available document templates
   */
  getTemplates: async () => {
    const response = await api.get('/api/documents/templates/');
    return response.data;
  },

  // Case documents (file uploads)
  /**
   * List all documents for a case
   */
  listDocuments: async (caseId: number) => {
    const response = await api.get(`/api/documents/cases/${caseId}/documents/`);
    return response.data;
  },

  /**
   * Upload document to a case
   * @param {number} caseId - Case ID
   * @param {FormData} formData - FormData with 'file' and 'kind' fields
   */
  uploadDocument: async (caseId: number, formData: FormData) => {
    const response = await api.post(
      `/api/documents/cases/${caseId}/documents/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete a document
   */
  deleteDocument: async (documentId: number) => {
    const response = await api.delete(`/api/documents/${documentId}/`);
    return response.data;
  },

  /**
   * Get single document details
   */
  getDocumentById: async (documentId: number) => {
    const response = await api.get(`/api/documents/${documentId}/`);
    return response.data;
  },

  /**
   * AI categorize a document
   * @param {number} documentId - Document ID
   * @returns {Promise} Response with category, confidence, reasoning
   */
  categorizeDocument: async (documentId: number) => {
    const response = await api.post(`/api/documents/${documentId}/categorize/`);
    return response.data;
  },

  /**
   * Get secure download URL for a document
   * Returns a short-lived signed URL (5 minutes) after authorization check.
   * @param {number} documentId - Document ID
   * @returns {Promise} Response with download_url, filename, mime
   */
  getDownloadUrl: async (documentId: number) => {
    const response = await api.get(`/api/documents/${documentId}/download/`);
    return response.data;
  },

  /**
   * Get inbound emails for a case
   * Returns list of emails received via email forwarding with attachment counts.
   * @param {number} caseId - Case ID
   * @returns {Promise} Response with emails array
   */
  getInboundEmails: async (caseId: number) => {
    const response = await api.get(`/api/documents/cases/${caseId}/inbound-emails/`);
    return response.data;
  },
};
